import React, { useEffect, useState } from "react";
import { Coins, Sparkles } from "lucide-react";
import { useTokens } from "./TokenContext";
import { useT } from "../context/LanguageContext";

export default function RewardOverlay() {
  const { t } = useT();
  const { reward, dismissReward, balance } = useTokens();
  const [animBalance, setAnimBalance] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (!reward) return;
    setShowCelebration(false);
    setAnimBalance(reward.balanceBefore || 0);
    // Briefly let the overlay mount, then trigger the tick.
    const t1 = setTimeout(() => setShowCelebration(true), 80);
    const t2 = setTimeout(() => setAnimBalance(reward.balanceAfter || balance || 0), 350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [reward, balance]);

  if (!reward) return null;

  const opponentLabel = reward.opponent
    ? `${t("v2.my.vs", { opp: reward.opponent })}`
    : (reward.simulated ? t("v2.rew.testWin") : t("v2.rew.matchWin"));
  const tokenLine = reward.tokensGained > 0
    ? (reward.tokensGained === 1 ? t("v2.rew.tokenLineOne") : t("v2.rew.tokenLine", { n: reward.tokensGained }))
    : t("v2.rew.pointsLine", { n: reward.points || 0 });

  return (
    <div
      data-testid="v2-reward-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-md p-6 animate-[reward-fade_.3s_ease-out]"
      onClick={dismissReward}
    >
      <style>{`
        @keyframes reward-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes reward-pop { 0% { transform: scale(0.6) rotate(-8deg); opacity: 0; } 50% { transform: scale(1.08) rotate(2deg); } 100% { transform: scale(1) rotate(0); opacity: 1; } }
        @keyframes coin-spin { 0% { transform: rotateY(0deg); } 100% { transform: rotateY(360deg); } }
        @keyframes spark-rise { 0% { opacity: 0; transform: translate(0, 0) scale(0.5); } 30% { opacity: 1; } 100% { opacity: 0; transform: translate(var(--dx, 0), var(--dy, -120px)) scale(1.1); } }
        @keyframes glow-pulse { 0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.15); } }
        .reward-card { animation: reward-pop .55s cubic-bezier(.34,1.56,.64,1) forwards; }
        .reward-coin { animation: coin-spin 2.6s linear infinite; transform-style: preserve-3d; }
        .reward-glow { animation: glow-pulse 2.4s ease-in-out infinite; }
      `}</style>

      <div
        className="relative w-full max-w-sm reward-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Particles */}
        {showCelebration && Array.from({ length: 18 }).map((_, i) => {
          const angle = (i / 18) * Math.PI * 2;
          const radius = 130 + (i % 3) * 30;
          const dx = `${Math.cos(angle) * radius}px`;
          const dy = `${Math.sin(angle) * radius}px`;
          const delay = `${(i % 6) * 50}ms`;
          return (
            <span
              key={i}
              className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-[#FCD34D] pointer-events-none"
              style={{
                "--dx": dx,
                "--dy": dy,
                animation: `spark-rise 1.4s ease-out ${delay} forwards`,
              }}
            />
          );
        })}

        {/* Halo behind coin */}
        <div className="absolute inset-0 -m-6 pointer-events-none">
          <div className="reward-glow absolute inset-0 rounded-full bg-[#F59E0B]/20 blur-3xl" />
        </div>

        <div className="relative bg-gradient-to-br from-[#1A1408] via-[#0F0902] to-[#0A0A0F] border border-[#F59E0B]/40 rounded-3xl p-7 text-center shadow-[0_0_60px_rgba(245,158,11,0.3)]">
          <div className="flex items-center justify-center gap-1 mb-4">
            <Sparkles className="w-4 h-4 text-[#FCD34D]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.4em] text-[#FCD34D]">{t("v2.rew.victory")}</span>
            <Sparkles className="w-4 h-4 text-[#FCD34D]" />
          </div>

          <div className="mx-auto w-28 h-28 relative">
            <div className="reward-coin absolute inset-0 rounded-full bg-gradient-to-br from-[#FFD700] via-[#F59E0B] to-[#92400E] ring-4 ring-[#FCD34D]/50 shadow-[0_0_40px_rgba(252,211,77,0.6)] flex items-center justify-center">
              <Coins className="w-12 h-12 text-[#3B2503]" strokeWidth={2.5} />
            </div>
          </div>

          <div className="mt-5 text-2xl font-bold text-white">{opponentLabel}</div>
          {reward.score && (
            <div className="text-sm font-mono text-[#71717A] mt-0.5">{reward.score}</div>
          )}

          <div className="mt-4">
            <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-[#FCD34D]/80">{tokenLine}</div>
            <div className="text-5xl font-bold text-[#FCD34D] tabular-nums mt-1 transition-all duration-500" data-testid="v2-reward-counter">
              {animBalance}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mt-0.5">{t("v2.rew.totalBalance")}</div>
          </div>

          <button
            onClick={dismissReward}
            data-testid="v2-reward-continue"
            className="mt-6 w-full bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] text-[#1A1408] font-bold uppercase tracking-wider rounded-2xl py-3 shadow-lg shadow-[#F59E0B]/30 hover:from-[#FCD34D] hover:to-[#FDE68A] transition-colors"
          >
            {t("v2.rew.continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
